import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiRequestError, type Json } from "../api.js";
import { Button } from "../payroll/components/ui/Button.js";
export const month = () => new Date().toISOString().slice(0, 7);
export const identifier = () => crypto.randomUUID();
export const text = (value: unknown) =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"
    ? String(value)
    : "—";

const INITIALISMS = new Map([
  ["api", "API"],
  ["cidr", "CIDR"],
  ["id", "ID"],
  ["ip", "IP"],
  ["mfa", "MFA"],
  ["oidc", "OIDC"],
  ["url", "URL"],
  ["uuid", "UUID"],
]);

export function friendlyLabel(value: string) {
  const words = value
    .replaceAll(/[_-]+/gu, " ")
    .replaceAll(/([a-z0-9])([A-Z])/gu, "$1 $2")
    .trim()
    .split(/\s+/u);
  return words
    .map((word, index) => {
      const initialism = INITIALISMS.get(word.toLowerCase());
      if (initialism !== undefined) return initialism;
      const normalized = word.toLowerCase();
      return index === 0
        ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
        : normalized;
    })
    .join(" ");
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/u;
const STATUS_COLUMNS = new Set(["mode", "outcome", "state", "status"]);

function TableScalar({ column, value }: { column: string; value: unknown }) {
  if (typeof value === "boolean")
    return (
      <span className="status-badge" data-tone={value ? "positive" : "neutral"}>
        {value ? "Yes" : "No"}
      </span>
    );
  if (typeof value === "string" && ISO_TIMESTAMP.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime()))
      return (
        <time dateTime={value} title={value}>
          {new Intl.DateTimeFormat("en-GB", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(date)}
        </time>
      );
  }
  if (typeof value === "string" && STATUS_COLUMNS.has(column)) {
    const normalized = value.toLowerCase();
    const tone = ["active", "allowed", "enabled", "succeeded"].includes(
      normalized,
    )
      ? "positive"
      : ["denied", "failed", "revoked", "suspended"].includes(normalized)
        ? "warning"
        : "neutral";
    return (
      <span className="status-badge" data-tone={tone}>
        {friendlyLabel(value)}
      </span>
    );
  }
  return text(value);
}
export const entries = (value: unknown, key: string): Json[] =>
  typeof value === "object" &&
  value !== null &&
  Array.isArray((value as Json)[key])
    ? ((value as Json)[key] as Json[])
    : [];
export function failure(error: unknown) {
  if (
    error instanceof ApiRequestError &&
    error.status === 503 &&
    error.code === "payroll_maintenance"
  )
    return "OpenProject was reached, but Work Profiles cannot be updated while payroll is in read-only maintenance mode.";
  if (
    error instanceof ApiRequestError &&
    error.code?.startsWith("payroll_settings_")
  )
    return error.message;
  if (error instanceof ApiRequestError && error.status === 409)
    return "This record changed. Refresh the current data and try again.";
  return error instanceof Error
    ? error.message
    : "The request could not be completed.";
}
export function Notice({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <p
      role={error ? "alert" : "status"}
      aria-live="polite"
      className={error ? "error" : "notice"}
    >
      {children}
    </p>
  );
}
export function useData<T extends Json>(
  load: () => Promise<T>,
  deps: readonly unknown[],
) {
  const [value, setValue] = useState<T>();
  const [error, setError] = useState<unknown>();
  const refresh = () => {
    setError(undefined);
    void load().then(setValue).catch(setError);
  };
  useEffect(refresh, deps);
  return { value, error, refresh };
}

const SECRET_VALUE_COLUMNS = new Set([
  "accesstoken",
  "apikey",
  "clientsecret",
  "credentialvalue",
  "keymaterial",
  "password",
  "passwordhash",
  "privatekey",
  "refreshtoken",
  "secret",
  "secretvalue",
  "token",
  "tokenhash",
]);

export function ObjectTable({
  title,
  rows,
  description,
  columns: selectedColumns,
  columnLabels = {},
  emptyMessage,
}: {
  title: string;
  rows: Json[];
  description?: string;
  columns?: string[];
  columnLabels?: Record<string, string>;
  emptyMessage?: string;
}) {
  if (!rows.length)
    return (
      <section className="table-empty" aria-label={title}>
        <h2>{title}</h2>
        {description !== undefined && <p>{description}</p>}
        <Notice>
          {emptyMessage ?? `No ${title.toLowerCase()} match this selection.`}
        </Notice>
      </section>
    );
  const availableColumns = [...new Set(rows.flatMap(Object.keys))].filter(
    (column) =>
      !SECRET_VALUE_COLUMNS.has(column.replaceAll(/[_-]/gu, "").toLowerCase()),
  );
  const columns = (selectedColumns ?? availableColumns).filter((column) =>
    availableColumns.includes(column),
  );
  return (
    <div
      className="table-wrap object-table-wrap"
      role="region"
      aria-label={title}
      tabIndex={0}
    >
      <table className="object-table">
        <caption>
          <span>{title}</span>
          {description !== undefined && <small>{description}</small>}
        </caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} data-column={column}>
                {columnLabels[column] ?? friendlyLabel(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={text(row["id"] ?? index)}>
              {columns.map((column) => (
                <td key={column} data-column={column}>
                  {Array.isArray(row[column]) ? (
                    <span className="data-token-list">
                      {(row[column] as unknown[]).map((value, valueIndex) => (
                        <span
                          className="data-token"
                          key={`${text(value)}-${String(valueIndex)}`}
                        >
                          {text(value)}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <TableScalar column={column} value={row[column]} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function ReasonAction({
  label,
  submit,
  children,
  description,
  disabled = false,
  compact = false,
}: {
  label: string;
  submit: (reason: string) => Promise<unknown>;
  children?: ReactNode;
  description?: string;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>();
  const first = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const [pending, setPending] = useState(false);
  return (
    <form
      className={`mutation${compact ? " mutation--compact" : ""}`}
      aria-label={label}
      onSubmit={(event) => {
        event.preventDefault();
        if (disabled || submitting.current) return;
        submitting.current = true;
        setPending(true);
        setError(undefined);
        void Promise.resolve()
          .then(() => submit(reason))
          .catch(setError)
          .finally(() => {
            submitting.current = false;
            setPending(false);
          });
      }}
    >
      {description !== undefined && (
        <p className="action-description">{description}</p>
      )}
      <label>
        Change reason
        <input
          ref={first}
          required
          minLength={8}
          placeholder="Explain why this change is needed"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        <small>
          {compact
            ? "Audit reason: at least 8 characters"
            : "Required for the audit log; enter at least 8 characters."}
        </small>
      </label>
      {children}
      <Button
        type="submit"
        variant="secondary"
        disabled={disabled || pending}
        loading={pending}
      >
        {label}
      </Button>
      {error !== undefined && <Notice error>{failure(error)}</Notice>}
    </form>
  );
}
